/**
 * Processing Pipeline Architecture
 *
 * This defines the complete processing pipeline for converting telemetry data
 * into fully processed domain objects.
 *
 * Supports two input sources:
 * 1. DTO Input - From backend API (TrackSessionDto)
 * 2. File Input - From device files (RawTelemetryData)
 *
 * Pipeline stages:
 * 1. Input Stage - Either DtoConversionStage or FileInputStage
 * 2. Raw Telemetry Processing - Convert raw readings to processed points
 * 3. Telemetry Synchronization - Align all telemetry to common time intervals
 * 4. Lap Detection - Identify lap boundaries from telemetry
 * 5. Lap Processing - Build lap objects (buildLap: lap-relative telemetry,
 *    statistics, spatial buckets, key points)
 * 6. Track Session Assembly - Combine all laps into track session
 */

import {
  TrackSessionDto,
  LapDto,
  Device,
  Guid,
  WeatherCondition as ApiWeatherCondition,
} from '../../api/apiTypes'
import {
  TrackSession,
  TrackInfo,
  SessionFeatures,
} from '../../domain/track-session/TrackSession'
import {
  TrackSessionMetadata,
  WeatherCondition,
} from '../../domain/track-session/TrackSessionMetadata'
import { TrackSessionStatus } from '../../domain/track-session/TrackSessionStatus'
import { Lap } from '../../domain/lap/Lap'
import { Track } from '../../domain/track/Track'
import { RawTelemetryData } from '../../domain/telemetry/raw/RawTelemetryData'
import { ProcessedTelemetryData } from '../../domain/telemetry/processed/ProcessedTelemetryData'
import { LapBounds } from '../../domain/lap/LapBounds'
import { generateUuid } from '../../utils/guidUtils'

/**
 * Pipeline configuration options
 */
export interface PipelineOptions {
  /** Enable lean angle calculation */
  calculateLeanAngle?: boolean
  /** Enable acceleration calculation */
  calculateAcceleration?: boolean
  /** Enable g-force calculation */
  calculateGForce?: boolean
  /** Synchronization interval in milliseconds */
  syncIntervalMs?: number
  /** Enable telemetry smoothing */
  enableSmoothing?: boolean
  /** Smoothing window size */
  smoothingWindow?: number
  /** Progress callback */
  onProgress?: (stage: string, progress: number) => void
  /**
   * Skip lap detection, processing, analysis, and spatial stages.
   * Use when applyStartFinishForSession() will be called after the pipeline,
   * since it re-detects laps and re-runs analysis/spatial processing anyway.
   * This avoids ~50% redundant processing on mobile devices.
   */
  skipLapStages?: boolean
}

/**
 * Default pipeline options
 */
export const DEFAULT_PIPELINE_OPTIONS: Required<PipelineOptions> = {
  calculateLeanAngle: true,
  calculateAcceleration: true,
  calculateGForce: true,
  syncIntervalMs: 100,
  enableSmoothing: false,
  smoothingWindow: 5,
  onProgress: () => {},
  skipLapStages: false,
}

/**
 * Pipeline stage result
 */
export interface StageResult<T> {
  success: boolean
  data?: T
  error?: string
  warnings?: string[]
}

/**
 * Complete pipeline result
 */
export interface PipelineResult {
  trackSession: TrackSession
  processingMetadata: ProcessingMetadata
}

/**
 * Processing metadata
 */
export interface ProcessingMetadata {
  processedAt: Date
  processingTimeMs: number
  stages: StageExecutionInfo[]
  options: PipelineOptions
  dataQuality: DataQualityMetrics
}

/**
 * Stage execution information
 */
export interface StageExecutionInfo {
  stageName: string
  executionTimeMs: number
  success: boolean
  warnings?: string[]
}

/**
 * Data quality metrics
 */
export interface DataQualityMetrics {
  hasGps: boolean
  hasGyroscope: boolean
  hasAccelerometer: boolean
  gpsPointCount: number
  gyroPointCount?: number
  accelPointCount?: number
  averageSamplingFrequency: number
  dataGaps: DataGap[]
}

/**
 * Data gap information
 */
export interface DataGap {
  startTime: Date
  endTime: Date
  durationMs: number
  type: 'gps' | 'gyroscope' | 'accelerometer'
}

/**
 * Input source discriminator for pipeline context
 */
export type InputSource =
  | { type: 'dto'; dto: TrackSessionDto }
  | { type: 'file'; fileName: string; sessionId: string }

/**
 * Session metadata input for file-based processing
 */
export interface FileInputMetadata {
  sessionId: string
  fileName: string
  driverName?: string
  vehicleId?: string
  vehicleName?: string
  weather?: WeatherCondition
  notes?: string
  device: Device
  sessionFeatures?: SessionFeatures
}

/**
 * Processing pipeline context
 * Carries data through pipeline stages
 */
export interface PipelineContext {
  // Input source (dto or file)
  inputSource: InputSource

  // Track information
  track: Track

  // Lap DTOs (if available from backend)
  lapDtos?: LapDto[]

  // Lap identifiers from backend (for ID preservation)
  lapsIdentifiers?: Record<number, Guid>

  // File input metadata (for file-based processing)
  fileMetadata?: FileInputMetadata

  // Legacy: keep dto for backward compatibility with existing stages
  dto: TrackSessionDto

  // Intermediate results
  rawTelemetry?: RawTelemetryData
  processedTelemetry?: ProcessedTelemetryData
  synchronizedTelemetry?: ProcessedTelemetryData
  lapBounds?: LapBounds[]
  laps?: Lap[]

  // Metadata
  options: Required<PipelineOptions>
  warnings: string[]
  stageResults: StageExecutionInfo[]
}

/**
 * Pipeline stage interface
 */
export interface PipelineStage {
  name: string
  execute(context: PipelineContext): Promise<StageResult<void>>
}

/**
 * Main processing pipeline executor
 */
export class TrackSessionProcessingPipeline {
  private options: Required<PipelineOptions>
  private stages: PipelineStage[]

  constructor(options: PipelineOptions = {}) {
    this.options = { ...DEFAULT_PIPELINE_OPTIONS, ...options }
    this.stages = []
  }

  /**
   * Add a processing stage
   */
  addStage(stage: PipelineStage): void {
    this.stages.push(stage)
  }

  /**
   * Execute the complete pipeline with DTO input
   */
  async execute(
    dto: TrackSessionDto,
    track: Track,
    lapDtos?: LapDto[]
  ): Promise<PipelineResult> {
    const context = this.createDtoContext(dto, track, lapDtos)
    return this.executeWithContext(context)
  }

  /**
   * Execute the pipeline with file input (raw telemetry)
   */
  async executeFromFile(
    track: Track,
    fileMetadata: FileInputMetadata
  ): Promise<PipelineResult> {
    const context = this.createFileContext(track, fileMetadata)
    return this.executeWithContext(context)
  }

  /**
   * Create context for DTO-based input
   */
  private createDtoContext(
    dto: TrackSessionDto,
    track: Track,
    lapDtos?: LapDto[]
  ): PipelineContext {
    return {
      inputSource: { type: 'dto', dto },
      dto, // Legacy field
      track,
      lapDtos,
      lapsIdentifiers: dto.laps,
      options: this.options,
      warnings: [],
      stageResults: [],
    }
  }

  /**
   * Create context for file-based input
   */
  private createFileContext(
    track: Track,
    fileMetadata: FileInputMetadata
  ): PipelineContext {
    // Create a minimal DTO for compatibility with existing stages
    const minimalDto: TrackSessionDto = {
      id: fileMetadata.sessionId as Guid,
      trackId: track.id as Guid,
      name: fileMetadata.fileName,
      metadata: {
        sessionStartTime: new Date(),
        weather: fileMetadata.weather
          ? {
              condition: fileMetadata.weather as unknown as ApiWeatherCondition,
            }
          : undefined,
      },
      telemetry: {
        device: fileMetadata.device,
        gps: { positions: [] },
      },
      laps: {},
    }

    return {
      inputSource: {
        type: 'file',
        fileName: fileMetadata.fileName,
        sessionId: fileMetadata.sessionId,
      },
      dto: minimalDto, // Legacy field for compatibility
      track,
      fileMetadata,
      options: this.options,
      warnings: [],
      stageResults: [],
    }
  }

  /**
   * Execute pipeline with prepared context
   */
  private async executeWithContext(
    context: PipelineContext
  ): Promise<PipelineResult> {
    const startTime = Date.now()

    // Execute each stage
    for (const stage of this.stages) {
      const stageStartTime = Date.now()
      this.options.onProgress(stage.name, 0)

      try {
        const result = await stage.execute(context)
        const executionTime = Date.now() - stageStartTime

        context.stageResults.push({
          stageName: stage.name,
          executionTimeMs: executionTime,
          success: result.success,
          warnings: result.warnings,
        })

        if (!result.success) {
          throw new Error(`Stage ${stage.name} failed: ${result.error}`)
        }

        if (result.warnings && result.warnings.length > 0) {
          context.warnings.push(...result.warnings)
        }
      } catch (error) {
        throw new Error(
          `Pipeline failed at stage ${stage.name}: ${error instanceof Error ? error.message : String(error)}`
        )
      }

      this.options.onProgress(stage.name, 100)
    }

    // Build final track session
    const trackSession = this.assembleTrackSession(context)

    const processingMetadata: ProcessingMetadata = {
      processedAt: new Date(),
      processingTimeMs: Date.now() - startTime,
      stages: context.stageResults,
      options: this.options,
      dataQuality: this.calculateDataQuality(context),
    }

    return {
      trackSession,
      processingMetadata,
    }
  }

  /**
   * Assemble the final track session from context
   */
  private assembleTrackSession(context: PipelineContext): TrackSession {
    const { track, laps, rawTelemetry, inputSource, fileMetadata } = context

    // Prefer synchronized telemetry when available (all arrays same length)
    const processedTelemetry =
      context.synchronizedTelemetry ?? context.processedTelemetry

    if (!processedTelemetry) {
      throw new Error(
        'Processed telemetry is required to create a TrackSession'
      )
    }

    if (!rawTelemetry) {
      throw new Error('Raw telemetry is required to create a TrackSession')
    }

    // Determine device info based on input source
    let device: Device
    let sessionId: string
    let sessionName: string
    let driverName: string
    let recordingDate: Date
    let weather: WeatherCondition | undefined
    let vehicleId: string | undefined
    let vehicleName: string | undefined
    let sourceFileName: string | undefined
    let sessionFeatures: SessionFeatures
    let isFromBackend: boolean

    if (inputSource.type === 'dto') {
      const dto = inputSource.dto
      device = {
        name: dto.telemetry?.device?.name ?? 'Unknown',
        version: dto.telemetry?.device?.version,
      }
      sessionId = dto.id
      sessionName = dto.name ?? 'Unnamed Session'
      driverName = 'Unknown' // DTO doesn't contain driver info
      // Convert from string to Date if needed (handle null metadata)
      const startTime = dto.metadata?.sessionStartTime
      recordingDate = startTime
        ? startTime instanceof Date
          ? startTime
          : new Date(startTime)
        : new Date()
      weather = dto.metadata?.weather?.condition as WeatherCondition | undefined
      sourceFileName = dto.name
      sessionFeatures = {
        hasVideo: false,
        hasLeanAngle: processedTelemetry.leanAngle.length > 0,
      }
      isFromBackend = true
    } else {
      // File input
      device = fileMetadata?.device ?? { name: 'Unknown', version: '1.0' }
      sessionId = inputSource.sessionId
      sessionName = inputSource.fileName
      driverName = fileMetadata?.driverName ?? 'Unknown'
      recordingDate =
        rawTelemetry.recordingStartTime ??
        processedTelemetry.gpsPoints[0]?.timestamp ??
        new Date()
      weather = fileMetadata?.weather
      vehicleId = fileMetadata?.vehicleId
      vehicleName = fileMetadata?.vehicleName
      sourceFileName = inputSource.fileName
      sessionFeatures = fileMetadata?.sessionFeatures ?? {
        hasVideo: false,
        hasLeanAngle: processedTelemetry.leanAngle.length > 0,
      }
      isFromBackend = false
    }

    // Convert domain Track to TrackInfo
    const trackInfo: TrackInfo = {
      id: track.id,
      name: track.name,
      finishLine: track.finishLine
        ? [
            {
              latitude: track.finishLine.startPoint.latitude,
              longitude: track.finishLine.startPoint.longitude,
            },
            {
              latitude: track.finishLine.endPoint.latitude,
              longitude: track.finishLine.endPoint.longitude,
            },
          ]
        : undefined,
      countryCode: track.location?.country,
      isUserSuggested: track.isUserSuggested,
    }

    const metadata: TrackSessionMetadata = {
      sessionId,
      sessionName,
      driverName,
      recordingDate,
      vehicleId,
      vehicleName,
      weather,
      sourceFileName,
    }

    const trackSession = new TrackSession(
      metadata,
      trackInfo,
      processedTelemetry,
      rawTelemetry,
      device,
      sessionFeatures,
      isFromBackend
        ? TrackSessionStatus.Completed
        : TrackSessionStatus.Processing
    )

    // Add all laps
    laps?.forEach((lap) => trackSession.addLap(lap))

    // Store lap identifiers if from backend
    if (context.lapsIdentifiers) {
      trackSession.lapsIdentifiers = context.lapsIdentifiers
    }

    // Store metadata if from DTO
    if (inputSource.type === 'dto') {
      trackSession.metaData = inputSource.dto.metadata
      trackSession.markAsStoredInBackend()
    }

    return trackSession
  }

  /**
   * Calculate data quality metrics
   */
  private calculateDataQuality(context: PipelineContext): DataQualityMetrics {
    const raw = context.rawTelemetry

    if (!raw) {
      return {
        hasGps: false,
        hasGyroscope: false,
        hasAccelerometer: false,
        gpsPointCount: 0,
        averageSamplingFrequency: 0,
        dataGaps: [],
      }
    }

    const processed = context.processedTelemetry

    return {
      hasGps: raw.gps.length > 0,
      hasGyroscope: (raw.gyroscope?.length ?? 0) > 0,
      hasAccelerometer: (raw.accelerometer?.length ?? 0) > 0,
      gpsPointCount: raw.gps.length,
      gyroPointCount: raw.gyroscope?.length,
      accelPointCount: raw.accelerometer?.length,
      averageSamplingFrequency: processed?.statistics.samplingFrequencyHz ?? 0,
      dataGaps: [], // TODO: Implement gap detection
    }
  }
}
